import React, { useEffect, useCallback, useState } from 'react';
import DireccionForm from './direccionForm';
import TipoPropiedadForm from './tipoPropiedadForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useGarantias } from '../../hooks/useGarantias';
import { direccionGarantia } from '../../models/garantias';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import { Grid2, Typography } from '@mui/material';
import { Close, Edit, SaveAlt } from '@mui/icons-material';
import { copyGarantia, postDireccionGarantia } from '../../services/servicesGarantias';
import { StatusCode, TipoFuncionalidad, TipoPropiedad } from '../../../../utils/enums';
import * as Yup from 'yup';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { useToast } from '../../../../hooks/useToast';
import Validaciones from '../../../../utils/validaciones';
import validaRut from '../../../../utils/validaRut';
import { format } from 'rut.js';
import { DetalleMutuoPermissionGuard } from '../../../../components/auth/DetalleMutuoPermissionGuard';
interface PropiedadDireccionFormProps {
    idMutuo: number;
    isComplementario: boolean;
    handleAfterCopy: any;
    idSolicitud: string | undefined;
    isDesactivado?: boolean;
    esVigente?: boolean;
    permisoCarteraMutuoEditarInfo?: boolean;
}

export default function PropiedadDireccionForm({ idMutuo, isComplementario, handleAfterCopy, idSolicitud, isDesactivado, esVigente, permisoCarteraMutuoEditarInfo }: PropiedadDireccionFormProps) {

    const schemaDireccionGarantia = Yup.object<direccionGarantia>().shape({
        direccionParticular: Yup.string().required('Campo requerido').max(100, 'Máximo 100 caracteres')
            .test('valida-direccion', 'Caractéres inválidos', function (value) {
                return Validaciones.letrasNumerosGuionGuionBajo(value ?? '');
            }),
        ciudad: Yup.string().required('Campo requerido'),
        comuna: Yup.string().required('Campo requerido'),
        rolPropiedad: Yup.string().required('Campo requerido').max(50, 'Máximo 50 caracteres'),
        tipoPropiedad: Yup.string().required('Campo requerido'),
        rutVendedor: Yup.string().nullable()
            .when(['tipoPropiedad'], {
                is: (tipoPropiedad: string) => tipoPropiedad === TipoPropiedad.Usada,
                then: s => s.required('Campo requerido'),
                otherwise: s => s.notRequired()
            }).test('valida-rut', 'RUT inválido', function (value) {
                return validaRut.rutFormat(value ?? '');
            }),
        nombreVendedor: Yup.string().nullable().max(50, 'Máximo 50 caracteres')
            .test('valida-nombre', 'Caractéres inválidos', function (value) {
                return Validaciones.soloLetras(value ?? '');
            }).when(['tipoPropiedad'], {
                is: (tipoPropiedad: string) => tipoPropiedad === TipoPropiedad.Usada,
                then: s => s.required('Campo requerido'),
                otherwise: s => s.notRequired()
            }),
        emailVendedor: Yup.string().nullable().max(50, 'Máximo 50 caracteres')
            .test('valida-rut', 'Email inválido', function (value) {
                return Validaciones.esValidoEmail(value ?? '');
            }).when(['tipoPropiedad'], {
                is: (tipoPropiedad: string) => tipoPropiedad === TipoPropiedad.Usada,
                then: s => s.required('Campo requerido'),
                otherwise: s => s.notRequired()
            }),
        otrosVendedores: Yup.string().nullable().max(1000, 'Máximo 1000 caracteres'),

        rutInmobiliaria: Yup.string().nullable()
            .test('valida-rut', 'RUT inválido', function (value) {
                return validaRut.rutFormat(value);
            }).when(['tipoPropiedad'], {
                is: (tipoPropiedad: string) => tipoPropiedad === TipoPropiedad.Nueva,
                then: s => s.required('Campo requerido'),
                otherwise: s => s.notRequired()
            }),
        nombreInmobiliaria: Yup.string().nullable().when(['tipoPropiedad'], {
            is: (tipoPropiedad: string) => tipoPropiedad === TipoPropiedad.Nueva,
            then: s => s.required('Campo requerido'),
            otherwise: s => s.notRequired()
        }),
    });

    const {
        listEstadoBienRaiz,
        hipotecas,
        comunas,
        ciudades,
        listaTipoPropiedad,
        tipoPropiedadDireccion,
        listarTipoPropiedad,
        comunaPorId,
        obtenerDatosGarantiaDireccion,
        obtenerCiudades,
    } = useGarantias();
    const {
        formState: { errors, isDirty, isValid },
        reset,
        control,
        setValue,
        watch,
        handleSubmit,
        trigger
    } = useForm<direccionGarantia>({
        resolver: yupResolver(schemaDireccionGarantia),
        defaultValues: tipoPropiedadDireccion,
        mode: 'onChange'
    });
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const { tipoPropiedad } = watch();
    const { addToast } = useToast();
    const inicializarDatos = useCallback(async () => {
        await obtenerDatosGarantiaDireccion(idMutuo, reset);
        await obtenerCiudades();
    }, [idMutuo, obtenerDatosGarantiaDireccion, reset, obtenerCiudades]);

    useEffect(() => {
        inicializarDatos();
    }, [idMutuo]);

    useEffect(() => {
        if (Object.keys(tipoPropiedadDireccion).length > 0) {
            reset(tipoPropiedadDireccion);
        }
    }, [tipoPropiedadDireccion, reset]);

    const handleChangeTipoPropiedad = useCallback(() => {
        if (tipoPropiedad === TipoPropiedad.Usada) {
            setValue('rutInmobiliaria', null);
            setValue('nombreInmobiliaria', null);
            trigger(['rutVendedor', 'nombreVendedor', 'emailVendedor', 'otrosVendedores']);

        } else if (tipoPropiedad === TipoPropiedad.Nueva) {
            setValue('rutVendedor', null);
            setValue('nombreVendedor', null);
            setValue('emailVendedor', null);
            setValue('otrosVendedores', null);
            trigger(['rutInmobiliaria', 'nombreInmobiliaria']);

        }
    }, [tipoPropiedad, trigger, setValue]);

    useEffect(() => {
        handleChangeTipoPropiedad();
    }, [handleChangeTipoPropiedad, tipoPropiedad]);

    const handleCancel = () => {
        setIsEditing(false);
        reset();
    }

    const handleCopy = () => {
        setModalConfirmacion({
            show: true,
            title: "Generar Copia",
            message: '¿Desea copiar la información de la garantía desde el mutuo normal?',
            metodo: () => {
                onCopy();
            },
        });
    }

    const onCopy = async () => {
        if (idSolicitud != null) {
            const copied = await copyGarantia(idSolicitud);
            if (copied) {
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: 'Datos copiados correctamente'
                });

                inicializarDatos();
                reset();
                handleAfterCopy();
            }
        }
    }

    const handleCancelEdition = () => {

        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: "¿Desea abandonar la edición?",
                metodo: () => {
                    handleCancel();
                },
            });
        }
        else {
            handleCancel();
        }
    }

    const onSubmit = useCallback(async (data: direccionGarantia) => {
        if (data.rutVendedor) {
            data.rutVendedor = format(data.rutVendedor, { dots: false });
        }
        if (data.rutInmobiliaria) {
            data.rutInmobiliaria = format(data.rutInmobiliaria, { dots: false });
        }
        data.idMutuo = idMutuo;
        const response = await postDireccionGarantia(data);
        if (response.status === StatusCode.Ok) {
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Datos modificados correctamente'
            });
            await obtenerDatosGarantiaDireccion(idMutuo, reset);
            handleAfterCopy();
            setIsEditing(false);
        }
        handleChangeTipoPropiedad();
    }, [idMutuo, obtenerDatosGarantiaDireccion, reset, addToast, handleChangeTipoPropiedad, handleAfterCopy]);

    const isReadonlyWithPermission = () => {
        let result = !isEditing || (esVigente && !permisoCarteraMutuoEditarInfo);
        return result ?? true;
    }
    return (
        <div>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid2 container alignItems="center" spacing={2}>
                    <Grid2 size={2}>
                        <Typography className='card-title' variant="h6">Datos garantía</Typography>
                    </Grid2>
                    <Grid2 spacing={1} size={10} container justifyContent="flex-end" mt={2}>
                        {!isComplementario && !isEditing ?
                            <DetalleMutuoPermissionGuard
                                esVigente={esVigente}
                                permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>
                                <MButton type="button" variant="contained" color="primary" startIcon={<Edit />} disabled={isDesactivado} onClick={() => { setIsEditing(true); trigger(); }}>Editar</MButton>
                            </DetalleMutuoPermissionGuard>
                            :
                            !isComplementario ? <>
                                <MButton type="submit" variant="contained" color="primary" startIcon={<SaveAlt />} disabled={!(isDirty && isValid)}>Guardar</MButton>
                                <MButton type="button" variant="contained" color="error" startIcon={<Close />} onClick={() => handleCancelEdition()}>Cancelar</MButton>
                            </>
                                :
                                <DetalleMutuoPermissionGuard
                                    esVigente={esVigente}
                                    permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info, TipoFuncionalidad.Cartera_editar_contacto]}>
                                    <MButton type="button" variant="contained" color="primary" startIcon={<Edit />} onClick={() => { handleCopy() }}>Copiar Datos Desde Mutuo Normal</MButton>
                                </DetalleMutuoPermissionGuard>
                        }
                    </Grid2>
                </Grid2>
                <DireccionForm
                    errors={errors}
                    isEditing={isEditing}
                    control={control}
                    ciudades={ciudades}
                    comunas={comunas}
                    hipotecas={hipotecas}
                    listEstadoBienRaiz={listEstadoBienRaiz}
                    comunaPorId={comunaPorId}
                    setValue={setValue}
                    esVigente={esVigente}
                    permisoCarteraMutuoEditarInfo={permisoCarteraMutuoEditarInfo ?? false}
                />
                <TipoPropiedadForm
                    trigger={trigger}
                    isEditing={isEditing}
                    control={control}
                    listaTipoPropiedad={listaTipoPropiedad}
                    listarTipoPropiedad={listarTipoPropiedad}
                    tipoPropiedad={tipoPropiedad}
                    setValue={setValue}
                    errors={errors}
                    esVigente={esVigente}
                    permisoCarteraMutuoEditarInfo={permisoCarteraMutuoEditarInfo ?? false}
                    isReadonlyWithPermission={isReadonlyWithPermission()}
                />
            </form>
        </div>
    )
}
